import { useCallback, useEffect, useMemo, useRef } from 'react';
import cloneDeep from 'lodash/cloneDeep';
import { observer } from 'mobx-react-lite';
import { useStore } from 'hooks';
import { ApplicantSearchContext } from 'types';
import { newCustomStyles } from 'components';
import { ISelectValue } from 'components/ui';
import {
  StyledSelectAddress,
  StyledSelectArea,
  Wrapper,
} from './fias-select.styled';

interface IProps {
  collapsed: boolean;
}

type IFiasItem = { [key: string]: any } | null;

export const FiasSelect = observer(({ collapsed }: IProps) => {
  const {
    fias,
    createRequest,
    areas,
    houses,
    meters,
    tenants,
    telephony,
    aiRequests,
  } = useStore();
  const {
    requestData,
    anotherAddress,
    setManuallySelectedArea,
    setManuallySelectedHouse,
    setRequestData,
    setBooleanProperties,
    invalidateApplicantSearch,
    searchApplicantByAddress,
  } = createRequest;
  const { applicantType } = createRequest.applicant;
  const houseSelectionToken = useRef(0);
  const areaSelectionToken = useRef(0);

  const createHouseSelectionGuard = () => {
    const token = ++houseSelectionToken.current;

    return () => token === houseSelectionToken.current;
  };

  const createAreaSelectionGuard = () => {
    const token = ++areaSelectionToken.current;

    return () => token === areaSelectionToken.current;
  };

  const onTenantSelected = useCallback<
    NonNullable<ApplicantSearchContext['onTenantSelected']>
  >(
    async (memberId, _areaId, isCurrent) => {
      if (isCurrent()) {
        await tenants.getTenantSectors(memberId, isCurrent);
      }
    },
    [tenants]
  );

  const applicantSearchContext = useMemo<ApplicantSearchContext>(
    () => ({ onTenantSelected }),
    [onTenantSelected]
  );

  useEffect(() => {
    if (fias.statuses.fiasTree.readyToLoad) {
      fias.getTree();
    }
    // Только при монтировании компонента
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedHouse: IFiasItem = useMemo(
    () =>
      fias.addressesList.find(
        (item: { [key: string]: any }): boolean =>
          item.value === requestData.house?._id
      ) || null,
    [fias.addressesList, requestData.house?._id]
  );

  const selectedArea: IFiasItem = useMemo(
    () =>
      fias.currentHouseFlatList.find(
        (item) => item.value === requestData.area?._id
      ) || null,
    [fias.currentHouseFlatList, requestData.area?._id]
  );

  useEffect(() => {
    if (fias.statuses.areasByHouse.pending) {
      return;
    }

    requestData.house?._id && fias.getAreasByHouse(requestData.house?._id);
    // Зависимость не должна срабатывать при изменении состояния pending,
    // необходимо для отсутствия дублирования запросов вместе с changeHouse
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestData.house?._id]);

  const changeHouse = (houseId: string) => {
    const isCurrent = createHouseSelectionGuard();

    areaSelectionToken.current += 1;

    fias.clearAreas();
    invalidateApplicantSearch('address');

    !anotherAddress && setBooleanProperties('anotherAddress', true);
    setRequestData(
      Object.assign(cloneDeep(requestData), {
        area: { _id: '' },
        house: { _id: houseId },
      })
    );
    houses.getHouseData(houseId, isCurrent).then(() => {
      if (!isCurrent()) {
        return;
      }

      setManuallySelectedHouse(houses.houseData.dataIHouseFormat);
      telephony.callerInfo.setCallerAddress(houses.houseData.address);
    });
    fias.getAreasByHouse(houseId, isCurrent);
  };

  const changeArea = (newValue: any): void => {
    const isCurrent = createAreaSelectionGuard();

    setRequestData(
      Object.assign(cloneDeep(requestData), {
        area: { _id: newValue.value },
      })
    );
    setBooleanProperties('anotherAddress', false);
    areas.getAreaData(newValue.value, isCurrent).then(() => {
      if (!isCurrent()) {
        return;
      }

      telephony.callerInfo.setCallerNumber(areas.areaData.str_number_full);
      setManuallySelectedArea(areas.areaData.dataIAreaFormat);
      meters.getAreaMetersAllReadings({
        area: areas.areaData.dataIAreaFormat.id,
      });
    });
    searchApplicantByAddress(newValue.value, applicantSearchContext);
  };

  const filterOption = useCallback((option: ISelectValue, value: string) => {
    const input = value?.toLowerCase().trim();

    if (!input) {
      return true;
    }

    return String(option.label).toLowerCase().includes(input);
  }, []);

  return (
    <Wrapper>
      <StyledSelectAddress
        aria-label="Адрес"
        noTenant={applicantType !== 'tenant'}
        collapsed={collapsed}
        fulfilled={!!selectedHouse}
        styles={newCustomStyles}
        options={fias.addressesList}
        placeholder={'Выберите адрес'}
        menuPortalTarget={document.body}
        closeMenuOnScroll={true}
        closeMenuOnSelect={true}
        className={'address'}
        classNamePrefix={'address'}
        menuPlacement={'auto'}
        isMulti={false}
        value={selectedHouse}
        onChange={(newValue: any) => changeHouse(newValue.value)}
        isDisabled={aiRequests.isAiMode}
      />

      {applicantType === 'tenant' && (
        <StyledSelectArea
          aria-label="Квартира"
          collapsed={collapsed}
          fulfilled={!!selectedArea}
          styles={newCustomStyles}
          options={fias.currentHouseFlatList}
          filterOption={filterOption}
          placeholder={
            fias.statuses.areasByHouse.pending ? 'Ищем квартиры' : 'Кв.'
          }
          menuPortalTarget={document.body}
          closeMenuOnScroll={true}
          closeMenuOnSelect={true}
          className={'area'}
          classNamePrefix={'area'}
          isDisabled={
            !selectedHouse ||
            !fias.statuses.areasByHouse.fulfilled ||
            fias.statuses.areasByHouse.pending ||
            aiRequests.isAiMode
          }
          menuPlacement={'auto'}
          isMulti={false}
          value={selectedArea}
          onChange={(newValue: any) => changeArea(newValue)}
        />
      )}
    </Wrapper>
  );
});
